import { Plus, CalendarClock } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Label } from '@/components/ui/label'
import { useCltWizardStore } from '@/stores/useCltWizardStore'
import { useMilestones } from '@/hooks/useCltReference'
import { BiweeklyOffsetGroupCard } from './BiweeklyOffsetGroupCard'
import { CltStepHeader } from './CltStepHeader'
import { type Labeled } from './clt.mock'
import { WeekSelector } from './WeekSelector'

export function StepSchedule() {
  const appliesToWeeks = useCltWizardStore(s => s.appliesToWeeks)
  const toggleWeek = useCltWizardStore(s => s.toggleWeek)
  const setWeeks = useCltWizardStore(s => s.setWeeks)
  const biweeklyOffsetGroups = useCltWizardStore(s => s.biweeklyOffsetGroups)
  const strategyGroups = useCltWizardStore(s => s.strategyGroups)
  const addBiweeklyOffsetGroup = useCltWizardStore(
    s => s.addBiweeklyOffsetGroup
  )
  const removeBiweeklyOffsetGroup = useCltWizardStore(
    s => s.removeBiweeklyOffsetGroup
  )
  const { data: milestoneList = [] } = useMilestones()

  // Matrix rules use the milestone label as the foreign key (see MatrixGrid.tsx
  // comment). Mirror that here so a biweekly group's milestone_ids line up with
  // the same ids the rules use.
  const milestones: Labeled[] = useMemo(
    () => milestoneList.map(m => ({ id: m.label, label: m.label })),
    [milestoneList]
  )

  const regularSelected = appliesToWeeks.filter(w => w <= 52).length
  const leapSelected = appliesToWeeks.includes(53)

  // Track which group id was just added so we open it expanded by default.
  const [lastAddedId, setLastAddedId] = useState<string | null>(null)
  const handleAdd = () => {
    const beforeIds = new Set(biweeklyOffsetGroups.map(g => g.id))
    addBiweeklyOffsetGroup()
    // After the store has appended, identify the new group by id diff. The
    // store update is synchronous so this works on the next render.
    requestAnimationFrame(() => {
      const next = useCltWizardStore
        .getState()
        .biweeklyOffsetGroups.find(g => !beforeIds.has(g.id))
      if (next) setLastAddedId(next.id)
    })
  }

  return (
    <div className="mx-auto w-full max-w-[820px]">
      <CltStepHeader
        title="Biweekly Offsets"
        description="Choose which fiscal weeks this template applies to, then add one or more biweekly offset groups. Each group applies its offset to a list of (strategy group + lane) targets across the milestones you pick."
      />

      <div className="space-y-4">
        {/* Applies to weeks */}
        <div className="space-y-3 rounded-lg border border-border bg-card p-6">
          <div className="flex items-center justify-between">
            <Label>Applies to events with POG Set Date on weeks</Label>
            <span className="text-xs text-muted-foreground">
              {regularSelected} of 52 weeks{leapSelected ? ' + W53*' : ''}{' '}
              selected
            </span>
          </div>
          <WeekSelector
            selected={appliesToWeeks}
            onToggle={toggleWeek}
            onSetWeeks={setWeeks}
          />
        </div>

        {/* Biweekly offset groups */}
        <div className="space-y-3 rounded-lg border border-border bg-card p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Label className="text-base font-semibold">
                Biweekly offset groups
              </Label>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Each group applies its offset (in days) across the selected
                milestones to every (strategy group + lane) target you add.
                Overlaps are allowed.
              </p>
            </div>
            <button
              type="button"
              onClick={handleAdd}
              className="flex shrink-0 cursor-pointer items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted"
            >
              <Plus className="h-3.5 w-3.5" />
              Add biweekly group
            </button>
          </div>

          {biweeklyOffsetGroups.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-border bg-background py-10 text-center">
              <CalendarClock className="mb-2 h-6 w-6 text-muted-foreground" />
              <p className="text-sm font-medium text-foreground">
                No biweekly offset groups yet
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Click "Add biweekly group" to configure one.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {biweeklyOffsetGroups.map(group => (
                <BiweeklyOffsetGroupCard
                  key={group.id}
                  group={group}
                  milestones={milestones}
                  strategyGroups={strategyGroups}
                  defaultExpanded={group.id === lastAddedId}
                  onRemove={() => removeBiweeklyOffsetGroup(group.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
